/**
 * Texas Made Shade — Signature Collections
 *
 * Single source of truth for the customer-market "Series" architecture.
 * Each collection targets a distinct buyer / market segment. The Alamo Series
 * (Residential) is the launched flagship; the remaining series are live as
 * SEO-indexable stub pages ("Coming Soon") until their flagship content lands.
 */

export type CollectionStatus = "flagship" | "coming-soon";

export interface Collection {
  slug: string;
  path: string;
  seriesName: string;      // e.g. "Alamo Series"
  fullName: string;        // e.g. "Alamo Series™ — Residential Shade & Covers"
  tagline: string;         // one-line pitch
  audience: string;        // who it's for
  market: string;          // short market label (e.g. "Residential")
  accent: string;          // tailwind class or hex for accent color
  description: string;     // meta description (~160 chars)
  status: CollectionStatus;
  solutions: string[];     // bullet list of products/services in this series
  cities?: string[];       // localized city callouts
}

export const COLLECTIONS: Collection[] = [
  {
    slug: "alamo-series",
    path: "/alamo-series",
    seriesName: "Alamo Series",
    fullName: "Alamo Series™ — Residential Shade & Covers",
    tagline: "Built-in-Texas shade for Texas homes.",
    audience: "Homeowners across San Antonio, Austin, New Braunfels, and the Hill Country.",
    market: "Residential",
    accent: "#b3391f",
    description:
      "Alamo Series™ — Texas Made Shade's residential division. Custom-built awnings, patio covers, sail shades, drop curtains, and Solair® retractables for San Antonio, Austin, and Hill Country homes.",
    status: "flagship",
    solutions: [
      "Custom Awnings (Sunbrella® canvas & welded aluminum)",
      "Solair® Retractable Awnings",
      "Patio, Pergola & Gazebo Covers",
      "Sail Shades",
      "Exterior Vertical Shades / Drop Screens",
      "Outdoor Drop Curtains & Privacy Panels",
      "Grill, BBQ & Outdoor Kitchen Covers",
      "Gazebo Enclosures",
      "Pool & Spa Shade",
      "Outdoor Heating & Refrigerated Cooling",
    ],
    cities: [
      "San Antonio",
      "Austin",
      "New Braunfels",
      "Seguin",
      "Boerne",
      "San Marcos",
      "Schertz",
      "Cibolo",
      "Bulverde",
      "Helotes",
    ],
  },
  {
    slug: "hill-country-series",
    path: "/hill-country-series",
    seriesName: "Hill Country Series",
    fullName: "Hill Country Series™ — Outdoor Living & Premium Shade",
    tagline: "Premium outdoor living — retractables, screens, curtains, lighting, cooling & comfort.",
    audience:
      "Homeowners building premium outdoor living spaces across the Hill Country, Lake Austin, Boerne, and San Antonio.",
    market: "Outdoor Living & Premium Shade",
    accent: "#5b7c47",
    description:
      "Hill Country Series™ outdoor living & premium shade — Solair® retractables, motorized screens, curtains, lighting and misting for San Antonio & Austin.",
    status: "flagship",
    solutions: [
      "Solair® Retractable Awnings",
      "Motorized Patio Screens",
      "Exterior Vertical Shades",
      "Patio Curtains / Drop Curtains",
      "Sail Shades & Residential Shade Structures",
      "Outdoor Misting Cooling — Aeromist Fan Misting Systems",
      "Outdoor Lighting — powered by Bosso Smart Lighting",
      "Pergola, Patio & Landscape / Accent Lighting",
      "Smart Lighting Controls",
      "Motorized Louvered Pergolas",
      "Infrared Patio Heaters & Outdoor Heating",
      "Outdoor Heating & Refrigerated Cooling (Mini-Splits)",
      "Backyard & Patio Upholstery / Cushions",
      "Outdoor Living Packages",
    ],

    cities: ["Boerne", "Fredericksburg", "Kerrville", "Wimberley", "Dripping Springs", "Lake Travis"],
  },
  {
    slug: "bluebonnet-series",
    path: "/bluebonnet-series",
    seriesName: "Bluebonnet Series",
    fullName: "Bluebonnet Series™ — Tents, Vendor Booths & Outdoor Markets",
    tagline: "Shade that travels — tents, booths, market canopies and boutique setups.",
    audience:
      "Vendors, boutiques, food trucks, farmers markets, festivals and event organizers across Texas.",
    market: "Tents, Vendor Booths & Outdoor Markets",
    accent: "#3a5b8a",
    description:
      "Bluebonnet Series™ — tents, vendor booths and outdoor market shade from Texas Made Shade: commercial pop-up tents and canopies, custom printed tops and sidewalls, boutique setups, table covers, banners and flags, plus tent repair and replacement parts.",
    status: "flagship",
    solutions: [
      "Commercial Event Tents & Custom Canopies",
      "Commercial Pop-Up Tents & Canopies",
      "Custom Printed Tent Tops & Sidewalls",
      "Vendor Booth & Boutique Setups",
      "Farmers Market & Festival Shade",
      "Table Covers, Banners & Flags",
      "Tent Repair, Recover & Replacement Parts",
    ],
    cities: ["Austin", "San Antonio", "New Braunfels", "Seguin", "Gruene"],
  },



  {
    slug: "longhorn-series",
    path: "/longhorn-series",
    seriesName: "Longhorn Series",
    fullName: "Longhorn Series™ — Commercial",
    tagline: "Retail, restaurants, and commercial construction.",
    audience: "Retail centers, restaurants, offices, developers, GCs, and property managers.",
    market: "Commercial",
    accent: "#8a5a2b",
    description:
      "Longhorn Series™ — Texas Made Shade's commercial division. Retail shopping centers, restaurants, new construction, commercial modernization, and amusement and entertainment venues across Texas.",
    status: "flagship",
    solutions: [
      "Commercial Awnings",
      "Restaurant & Patio Awnings",
      "Backlit / Illuminated Awnings",
      "Metal & Standing-Seam Awnings",
      "Storefront & Walkway Canopies",
      "Commercial Drop Curtains",
      "Motorized Patio Screens",
      "Covered Parking",
      "Car Wash Shade Sails",
      "Outdoor Heating & Cooling for Commercial Patios",
      "Restaurant Patio Misting Systems",
    ],
    cities: ["San Antonio", "Austin", "Houston", "Corpus Christi"],
  },
  {
    slug: "rio-grande-series",
    path: "/rio-grande-series",
    seriesName: "Rio Grande Series",
    fullName: "Rio Grande Series™ — Hospitality & Resorts",
    tagline: "Hotels • Resorts • Glamping • Safari Tents • Spas • Retreats • Event Venues",
    audience: "Hotels, resorts, glamping operators, spas, ranch retreats, event venues, boutique lodging and hospitality patios.",
    market: "Hospitality",
    accent: "#c98032",
    description:
      "Rio Grande Series™ — hospitality & resort shade: resort pool cabanas, glamping and safari tents, spa and wellness shade, hotel patio awnings, restaurant drop curtains, and event-venue structures across Texas.",
    status: "flagship",
    solutions: [
      "Resort & Hotel Cabanas",
      "Glamping & Safari Tents",
      "Spa & Wellness Shade",
      "Restaurant Patio Enclosures",
      "Event & Venue Shade",
      "Poolside Drop Curtains",
      "Custom Glamping & Safari Tent Design and Fabrication",
      "Glamping Tent Repairs, Recover & Rebuilds",
      "Resort Misting, Heating & Outdoor Cooling",
    ],
    cities: ["San Antonio", "Austin", "Corpus Christi", "South Padre Island"],
  },
  {
    slug: "republic-series",
    path: "/republic-series",
    seriesName: "Republic Series",
    fullName: "Republic Series™ — Government, Schools & Public Works",
    tagline: "Bid-ready shade for schools, cities, and public agencies.",
    audience: "School districts, cities, counties, and government buyers.",
    market: "Government & Schools",
    accent: "#33487a",
    description:
      "Republic Series™ — bid-ready shade for Texas school districts, municipalities, and government agencies. Playground shade, athletic dugouts, and bus canopies built in Texas.",
    status: "flagship",
    solutions: [
      "Playground Shade",
      "School Shade Structures",
      "Church Shade Structures",
      "Athletic / Dugout / Bleacher Shade",
      "Covered Walkways & Bus Canopies",
      "School Windscreens & Pit Covers",
      "Municipal Awnings & Signage Canopies",
    ],
    cities: ["San Antonio ISD", "Austin ISD", "New Braunfels ISD", "Seguin ISD"],
  },
  {
    slug: "ranger-series",
    path: "/ranger-series",
    seriesName: "Ranger Series",
    fullName: "Ranger Series™ — Care, Maintenance & Protection Plans",
    tagline: "Cleaned, inspected and storm-ready — every single year.",
    audience: "Owners who want their awning, retractable system or cover maintained on a schedule.",
    market: "Care & Maintenance Plans",
    accent: "#6d6d6d",
    description:
      "Ranger Series™ — Texas Made Shade's care and maintenance division. Ranger Care™ membership plans cover annual fabric cleaning and protectant, hardware and motor inspection, storm prep and priority service scheduling. Repairs and recovers are handled by the Revival Series™.",
    status: "flagship",
    solutions: [
      "Ranger Care™ Annual Maintenance Memberships",
      "Fabric Cleaning & Protectant",
      "Hardware, Motor & Frame Inspection",
      "Storm Prep & Priority Scheduling",
    ],
    cities: ["San Antonio", "Austin", "New Braunfels", "Seguin", "Corpus Christi"],
  },
  {
    slug: "lone-star-elite",
    path: "/lone-star-elite",
    seriesName: "Lone Star Series",
    fullName: "Lone Star Series™ — Smart Lighting & Automation",
    tagline: "Smart lighting, motors, sensors and scenes — the outdoor system that runs itself.",
    audience: "Homeowners and businesses who want app-controlled outdoor lighting, shade and automation.",
    market: "Smart Lighting & Automation",
    accent: "#c9a84c",
    description:
      "Lone Star Series™ — Texas Made Shade's smart lighting and automation division. Bosso Smart Lighting, automated retractable awnings, motorized screens, automated exterior shades, lighting controls, app and remote control, and wind/sun sensors across Texas.",
    status: "flagship",
    solutions: [
      "Bosso Smart Lighting",
      "Automated Retractable Awnings",
      "Motorized Screens",
      "Automated Exterior Shades",
      "Lighting Controls",
      "Remote / App Control",
      "Wind & Sun Sensors",
      "Integrated Outdoor Living Controls",
    ],
    cities: ["San Antonio", "Austin", "Statewide"],
  },
];


export function getCollection(slug: string): Collection | undefined {
  return COLLECTIONS.find((c) => c.slug === slug);
}
